/**
 * UserProjectsView Component
 *
 * User-facing view for browsing projects:
 * - "My Projects" - projects owned by the current user
 * - Project cards with name, description, agent count
 * - Expandable cards with agent assignment
 *
 * @module components/user/UserProjectsView
 */

import { useState, useCallback, useEffect, memo } from 'react';
import {
  FolderKanban,
  RefreshCw,
  Search,
  AlertCircle,
  Bot,
  Archive,
  CheckCircle,
  Circle,
  Plus,
  X,
  Loader2,
  ChevronDown,
  ChevronUp,
  UserPlus,
  Trash2,
  Hash,
  Key,
  ExternalLink
} from 'lucide-react';
import { Avatar } from '../common/Avatar.jsx';
import CredentialForm from '../admin/CredentialForm.jsx';

// ============================================
// API Functions
// ============================================

async function fetchProjects() {
  const response = await fetch('/api/projects', {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch projects: ${response.statusText}`);
  }
  const data = await response.json();
  return data.data || [];
}

async function createProject({ name, displayName, description }) {
  const response = await fetch('/api/projects', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ name, displayName, description })
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const rawMessage = error.message || error.error || response.statusText;
    const errorMessage = typeof rawMessage === 'string' ? rawMessage : JSON.stringify(rawMessage);
    throw new Error(errorMessage);
  }
  return response.json();
}

async function fetchProjectAgents(projectId) {
  const response = await fetch(`/api/projects/${projectId}/agents`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch project agents: ${response.statusText}`);
  }
  const data = await response.json();
  return data.data || [];
}

async function fetchProjectChannels(projectId) {
  const response = await fetch(`/api/projects/${projectId}/channels`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch project channels: ${response.statusText}`);
  }
  const data = await response.json();
  return data.data || [];
}

async function fetchAvailableAgents() {
  const response = await fetch('/api/agents', {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch agents: ${response.statusText}`);
  }
  const data = await response.json();
  return data.data || [];
}

async function fetchRoles() {
  const response = await fetch('/api/roles', {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch roles: ${response.statusText}`);
  }
  const data = await response.json();
  return data.data || [];
}

async function assignAgentToProject(projectId, agentId, roleId) {
  const response = await fetch(`/api/projects/${projectId}/agents/${agentId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ roleId })
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const rawMessage = error.message || error.error || response.statusText;
    const errorMessage = typeof rawMessage === 'string' ? rawMessage : JSON.stringify(rawMessage);
    throw new Error(errorMessage);
  }
  return response.json();
}

async function removeAgentFromProject(projectId, agentId) {
  const response = await fetch(`/api/projects/${projectId}/agents/${agentId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const rawMessage = error.message || error.error || response.statusText;
    const errorMessage = typeof rawMessage === 'string' ? rawMessage : JSON.stringify(rawMessage);
    throw new Error(errorMessage);
  }
}

// ============================================
// Credentials API Functions
// ============================================

async function fetchProjectCredentials(projectId) {
  const params = new URLSearchParams();
  params.set('scope', 'project');
  params.set('projectId', projectId);

  const response = await fetch(`/api/credentials?${params}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch credentials: ${response.statusText}`);
  }
  const data = await response.json();
  return data.data || [];
}

async function createCredential(data) {
  const response = await fetch('/api/credentials', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to create credential');
  }
  return response.json();
}

async function updateCredential(id, data) {
  const response = await fetch(`/api/credentials/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to update credential');
  }
  return response.json();
}

async function deleteCredential(id) {
  const response = await fetch(`/api/credentials/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to delete credential');
  }
}

async function fetchAgentCredentials(agentId) {
  const params = new URLSearchParams();
  params.set('scope', 'agent');
  params.set('agentId', agentId);

  const response = await fetch(`/api/credentials?${params}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch agent credentials: ${response.statusText}`);
  }
  const data = await response.json();
  return data.data || [];
}

// ============================================
// Sub-Components
// ============================================

const SearchInput = memo(function SearchInput({
  value,
  onChange,
  placeholder = 'Search projects...'
}) {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="
          w-full pl-9 pr-3 py-2 rounded-md
          bg-gray-900 border border-gray-700 text-gray-100
          placeholder-gray-500
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
          transition-colors
        "
      />
    </div>
  );
});

const LoadingSkeleton = memo(function LoadingSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="p-4 rounded-lg bg-gray-800/50 animate-pulse"
        >
          <div className="h-5 w-32 bg-gray-700 rounded mb-3" />
          <div className="h-3 w-48 bg-gray-700 rounded mb-2" />
          <div className="h-3 w-24 bg-gray-700 rounded" />
        </div>
      ))}
    </div>
  );
});

/**
 * AgentCredentialRow - Expandable agent row with credentials
 */
const AgentCredentialRow = memo(function AgentCredentialRow({
  agent,
  isArchived,
  onRemove,
  isRemoving,
  agentCredentials,
  isLoadingCredentials,
  onLoadCredentials,
  onAddCredential,
  onEditCredential,
  onDeleteCredential
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = useCallback((e) => {
    e.stopPropagation();
    if (!isExpanded && !agentCredentials) {
      onLoadCredentials?.(agent.id);
    }
    setIsExpanded(!isExpanded);
  }, [isExpanded, agentCredentials, agent.id, onLoadCredentials]);

  return (
    <div className="rounded-md bg-gray-900/50 overflow-hidden">
      {/* Agent header row */}
      <div
        className="flex items-center justify-between p-2 cursor-pointer hover:bg-gray-800/50 transition-colors"
        onClick={handleToggle}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Avatar
            name={agent.displayName || agent.name}
            avatarUrl={agent.avatarUrl}
            type="agent"
            size="sm"
          />
          <span className="text-sm text-gray-300 truncate">
            {agent.displayName || agent.name}
          </span>
          {/* Credential count badge */}
          {agentCredentials && agentCredentials.length > 0 && (
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs bg-indigo-500/20 text-indigo-400">
              <Key className="w-3 h-3" />
              {agentCredentials.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!isArchived && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove?.(agent.id);
              }}
              disabled={isRemoving}
              className="p-1 rounded text-gray-500 hover:text-red-400 hover:bg-red-500/10 disabled:opacity-50 transition-colors"
              title="Remove agent"
            >
              {isRemoving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </button>
          )}
          <div className="p-1 text-gray-500">
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </div>
        </div>
      </div>

      {/* Expanded credentials section */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-1 border-t border-gray-800">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Agent Credentials</p>
            {!isArchived && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddCredential?.(agent);
                }}
                className="flex items-center gap-1 px-2 py-1 rounded text-xs text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Add
              </button>
            )}
          </div>
          {isLoadingCredentials ? (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            </div>
          ) : agentCredentials && agentCredentials.length > 0 ? (
            <div className="space-y-1.5">
              {agentCredentials.map((credential) => (
                <div
                  key={credential.id}
                  className="flex items-center justify-between p-2 rounded bg-gray-800/50 group"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Key className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm text-gray-300 truncate">{credential.name}</div>
                      <div className="text-xs text-gray-500">{credential.type}</div>
                    </div>
                  </div>
                  {!isArchived && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditCredential?.(agent, credential);
                        }}
                        className="p-1 text-gray-500 hover:text-indigo-400 transition-colors"
                        title="Edit"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteCredential?.(agent, credential);
                        }}
                        className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic py-1">No credentials for this agent</p>
          )}
        </div>
      )}
    </div>
  );
});

const ErrorState = memo(function ErrorState({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="p-3 rounded-full bg-red-500/10 mb-4">
        <AlertCircle className="w-8 h-8 text-red-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-300 mb-1">
        Failed to load projects
      </h3>
      <p className="text-sm text-red-400 mb-4">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="
            flex items-center gap-2 px-4 py-2 rounded-md
            text-gray-300 bg-gray-700 hover:bg-gray-600
            transition-colors
          "
        >
          <RefreshCw className="w-4 h-4" />
          Retry
        </button>
      )}
    </div>
  );
});

const EmptyState = memo(function EmptyState({ message, description }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="p-3 rounded-full bg-gray-800 mb-4">
        <FolderKanban className="w-8 h-8 text-gray-500" />
      </div>
      <h3 className="text-lg font-medium text-gray-300 mb-1">{message}</h3>
      <p className="text-sm text-gray-500">{description}</p>
    </div>
  );
});

const CreateProjectModal = memo(function CreateProjectModal({ isOpen, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    setIsSubmitting(true);
    setError(null);
    try {
      await onCreate({ name: name.trim(), displayName: displayName.trim() || undefined, description: description.trim() || undefined });
      setName('');
      setDisplayName('');
      setDescription('');
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-gray-900 border border-gray-700 rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">New Project</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 rounded-md bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="my-project"
              className="
                w-full px-3 py-2 rounded-md
                bg-gray-800 border border-gray-700 text-gray-100
                placeholder-gray-500
                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
              "
              required
              autoFocus
            />
            <p className="text-xs text-gray-500 mt-1">Unique identifier (lowercase, no spaces)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Display Name
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="My Project"
              className="
                w-full px-3 py-2 rounded-md
                bg-gray-800 border border-gray-700 text-gray-100
                placeholder-gray-500
                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
              "
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={3}
              className="
                w-full px-3 py-2 rounded-md
                bg-gray-800 border border-gray-700 text-gray-100
                placeholder-gray-500
                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                resize-none
              "
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="
                px-4 py-2 rounded-md
                text-gray-300 bg-gray-700 hover:bg-gray-600
                transition-colors
              "
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || isSubmitting}
              className="
                flex items-center gap-2 px-4 py-2 rounded-md
                text-white bg-indigo-600 hover:bg-indigo-500
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Create Project
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

const AssignAgentModal = memo(function AssignAgentModal({
  isOpen,
  onClose,
  project,
  availableAgents,
  assignedAgentIds,
  roles,
  onAssign
}) {
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Filter out already assigned agents
  const unassignedAgents = availableAgents.filter(
    agent => !assignedAgentIds.includes(agent.id)
  );

  // Filter to project-scoped and global roles
  const projectRoles = roles.filter(r => r.scope === 'project' || r.scope === 'global');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAgentId || !selectedRoleId) return;

    setIsSubmitting(true);
    setError(null);
    try {
      await onAssign(project.id, selectedAgentId, selectedRoleId);
      setSelectedAgentId('');
      setSelectedRoleId('');
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-gray-900 border border-gray-700 rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">Assign Agent</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 rounded-md bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Project
            </label>
            <div className="px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-gray-300">
              {project?.displayName || project?.name}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Select Agent <span className="text-red-400">*</span>
            </label>
            {unassignedAgents.length === 0 ? (
              <p className="text-sm text-gray-500 italic py-2">
                All agents are already assigned to this project.
              </p>
            ) : (
              <select
                value={selectedAgentId}
                onChange={(e) => setSelectedAgentId(e.target.value)}
                className="
                  w-full px-3 py-2 rounded-md
                  bg-gray-800 border border-gray-700 text-gray-100
                  focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                "
                required
              >
                <option value="">Select an agent...</option>
                {unassignedAgents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.displayName || agent.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Select Role <span className="text-red-400">*</span>
            </label>
            <select
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              className="
                w-full px-3 py-2 rounded-md
                bg-gray-800 border border-gray-700 text-gray-100
                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
              "
              required
            >
              <option value="">Select a role...</option>
              {projectRoles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.displayName || role.name}
                </option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="
                px-4 py-2 rounded-md
                text-gray-300 bg-gray-700 hover:bg-gray-600
                transition-colors
              "
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedAgentId || !selectedRoleId || isSubmitting}
              className="
                flex items-center gap-2 px-4 py-2 rounded-md
                text-white bg-indigo-600 hover:bg-indigo-500
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Assign Agent
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

const ProjectCard = memo(function ProjectCard({
  project,
  isExpanded,
  onToggleExpand,
  agents,
  channels,
  credentials,
  agentCredentials,
  isLoadingDetails,
  isLoadingCredentials,
  loadingAgentCredentialsId,
  onAssignAgent,
  onRemoveAgent,
  removingAgentId,
  onAddCredential,
  onEditCredential,
  onDeleteCredential,
  onLoadAgentCredentials,
  onAddAgentCredential,
  onEditAgentCredential,
  onDeleteAgentCredential
}) {
  const {
    name,
    displayName,
    description,
    isActive,
    isArchived,
    _count
  } = project;

  const agentCount = agents?.length ?? project.agentCount ?? _count?.agents ?? 0;

  return (
    <div
      className={`
        rounded-lg border overflow-hidden
        transition-colors
        ${isArchived
          ? 'bg-gray-800/30 border-gray-700/30 opacity-60'
          : 'bg-gray-800/50 border-gray-700/50'
        }
      `}
    >
      {/* Header - clickable */}
      <div
        className={`
          p-4 cursor-pointer
          ${!isArchived ? 'hover:bg-gray-800' : ''}
          transition-colors
        `}
        onClick={onToggleExpand}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <FolderKanban className={`w-5 h-5 ${isActive ? 'text-indigo-400' : 'text-gray-500'}`} />
            <h3 className="text-base font-semibold text-white truncate">
              {displayName || name}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {isArchived ? (
              <Archive className="w-4 h-4 text-gray-500" title="Archived" />
            ) : isActive ? (
              <CheckCircle className="w-4 h-4 text-green-500" title="Active" />
            ) : (
              <Circle className="w-4 h-4 text-gray-500" title="Inactive" />
            )}
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>

        {/* Description */}
        {description && (
          <p className="text-sm text-gray-400 line-clamp-2 mb-3">
            {description}
          </p>
        )}

        {/* Footer */}
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Bot className="w-3.5 h-3.5" />
            {agentCount} agent{agentCount !== 1 ? 's' : ''}
          </span>
          {channels && (
            <span className="flex items-center gap-1">
              <Hash className="w-3.5 h-3.5" />
              {channels.length} channel{channels.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-700/50 space-y-4">
          {isLoadingDetails ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {/* Agents section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Assigned Agents</p>
                  {!isArchived && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAssignAgent?.();
                      }}
                      className="
                        flex items-center gap-1 px-2 py-1 rounded text-xs
                        text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10
                        transition-colors
                      "
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                      Add
                    </button>
                  )}
                </div>
                {agents && agents.length > 0 ? (
                  <div className="space-y-2">
                    {agents.map((agent) => (
                      <AgentCredentialRow
                        key={agent.id}
                        agent={agent}
                        isArchived={isArchived}
                        onRemove={onRemoveAgent}
                        isRemoving={removingAgentId === agent.id}
                        agentCredentials={agentCredentials?.[agent.id]}
                        isLoadingCredentials={loadingAgentCredentialsId === agent.id}
                        onLoadCredentials={onLoadAgentCredentials}
                        onAddCredential={onAddAgentCredential}
                        onEditCredential={onEditAgentCredential}
                        onDeleteCredential={onDeleteAgentCredential}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No agents assigned</p>
                )}
              </div>

              {/* Channels section */}
              {channels && channels.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Channels</p>
                  <div className="flex flex-wrap gap-2">
                    {channels.map((channel) => (
                      <span
                        key={channel.id}
                        className="flex items-center gap-1 px-2 py-1 rounded-md bg-gray-900/50 text-sm text-gray-400"
                      >
                        <Hash className="w-3.5 h-3.5" />
                        {channel.displayName || channel.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Credentials section */}
              {!isArchived && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Project Credentials</p>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddCredential?.();
                      }}
                      className="
                        flex items-center gap-1 px-2 py-1 rounded text-xs
                        text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10
                        transition-colors
                      "
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add
                    </button>
                  </div>
                  {isLoadingCredentials ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    </div>
                  ) : credentials && credentials.length > 0 ? (
                    <div className="space-y-2">
                      {credentials.map((credential) => (
                        <div
                          key={credential.id}
                          className="flex items-center justify-between p-2 rounded-md bg-gray-900/50 group"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <Key className="w-4 h-4 text-gray-500 flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="text-sm text-gray-300 truncate">{credential.name}</div>
                              <div className="text-xs text-gray-500">{credential.type}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditCredential?.(credential);
                              }}
                              className="p-1 text-gray-500 hover:text-indigo-400 transition-colors"
                              title="Edit"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteCredential?.(credential);
                              }}
                              className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No credentials for this project</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
});

// ============================================
// Main Component
// ============================================

export default function UserProjectsView({ onSelectProject }) {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Expand/collapse state
  const [expandedProjectId, setExpandedProjectId] = useState(null);
  const [projectDetails, setProjectDetails] = useState({}); // { projectId: { agents, channels } }
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // Agent assignment state
  const [availableAgents, setAvailableAgents] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [assignModalProject, setAssignModalProject] = useState(null);
  const [removingAgentId, setRemovingAgentId] = useState(null);

  // Credential state (project-level)
  const [isLoadingCredentials, setIsLoadingCredentials] = useState(false);
  const [credentialModalProject, setCredentialModalProject] = useState(null);
  const [editingCredential, setEditingCredential] = useState(null);
  const [isCredentialFormOpen, setIsCredentialFormOpen] = useState(false);

  // Agent credential state
  const [agentCredentials, setAgentCredentials] = useState({}); // { agentId: credentials[] }
  const [loadingAgentCredentialsId, setLoadingAgentCredentialsId] = useState(null);
  const [credentialModalAgent, setCredentialModalAgent] = useState(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [projectsData, agentsData, rolesData] = await Promise.all([
        fetchProjects(),
        fetchAvailableAgents(),
        fetchRoles()
      ]);
      setProjects(projectsData);
      setAvailableAgents(agentsData);
      setAvailableRoles(rolesData);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load project details when expanded
  const loadProjectDetails = useCallback(async (projectId) => {
    if (projectDetails[projectId]) return; // Already loaded

    setIsLoadingDetails(true);
    setIsLoadingCredentials(true);
    try {
      const [agents, channels, credentials] = await Promise.all([
        fetchProjectAgents(projectId),
        fetchProjectChannels(projectId),
        fetchProjectCredentials(projectId)
      ]);
      setProjectDetails(prev => ({
        ...prev,
        [projectId]: { agents, channels, credentials }
      }));
    } catch (err) {
      console.error('Failed to load project details:', err);
    } finally {
      setIsLoadingDetails(false);
      setIsLoadingCredentials(false);
    }
  }, [projectDetails]);

  // Reload credentials for a specific project
  const reloadProjectCredentials = useCallback(async (projectId) => {
    setIsLoadingCredentials(true);
    try {
      const credentials = await fetchProjectCredentials(projectId);
      setProjectDetails(prev => ({
        ...prev,
        [projectId]: { ...prev[projectId], credentials }
      }));
    } catch (err) {
      console.error('Failed to reload credentials:', err);
    } finally {
      setIsLoadingCredentials(false);
    }
  }, []);

  const handleToggleExpand = useCallback((projectId) => {
    if (expandedProjectId === projectId) {
      setExpandedProjectId(null);
    } else {
      setExpandedProjectId(projectId);
      loadProjectDetails(projectId);
    }
  }, [expandedProjectId, loadProjectDetails]);

  const handleCreateProject = useCallback(async (projectData) => {
    await createProject(projectData);
    await loadData();
  }, [loadData]);

  const handleAssignAgent = useCallback(async (projectId, agentId, roleId) => {
    await assignAgentToProject(projectId, agentId, roleId);
    // Refresh project details
    const [agents, channels] = await Promise.all([
      fetchProjectAgents(projectId),
      fetchProjectChannels(projectId)
    ]);
    setProjectDetails(prev => ({
      ...prev,
      [projectId]: { agents, channels }
    }));
    // Also reload main data to update counts
    await loadData();
  }, [loadData]);

  const handleRemoveAgent = useCallback(async (projectId, agentId) => {
    setRemovingAgentId(agentId);
    try {
      await removeAgentFromProject(projectId, agentId);
      // Refresh project details
      const [agents, channels] = await Promise.all([
        fetchProjectAgents(projectId),
        fetchProjectChannels(projectId)
      ]);
      setProjectDetails(prev => ({
        ...prev,
        [projectId]: { agents, channels, credentials: prev[projectId]?.credentials }
      }));
      // Also reload main data to update counts
      await loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setRemovingAgentId(null);
    }
  }, [loadData]);

  // Credential handlers
  const handleAddCredential = useCallback((project) => {
    setCredentialModalProject(project);
    setEditingCredential(null);
    setIsCredentialFormOpen(true);
  }, []);

  const handleEditCredential = useCallback((project, credential) => {
    setCredentialModalProject(project);
    setEditingCredential(credential);
    setIsCredentialFormOpen(true);
  }, []);

  const handleDeleteCredential = useCallback(async (project, credential) => {
    if (!confirm(`Delete credential "${credential.name}"?`)) return;
    try {
      await deleteCredential(credential.id);
      await reloadProjectCredentials(project.id);
    } catch (err) {
      alert(err.message);
    }
  }, [reloadProjectCredentials]);

  const handleCredentialSave = useCallback(async (credentialData) => {
    try {
      if (editingCredential) {
        await updateCredential(editingCredential.id, credentialData);
      } else if (credentialModalAgent) {
        // Agent-scoped credential
        await createCredential({
          ...credentialData,
          scope: 'agent',
          agentId: credentialModalAgent.id
        });
      } else {
        // Project-scoped credential
        await createCredential({
          ...credentialData,
          scope: 'project',
          projectId: credentialModalProject.id
        });
      }

      // Reload appropriate credentials
      if (credentialModalAgent) {
        await reloadAgentCredentials(credentialModalAgent.id);
      } else {
        await reloadProjectCredentials(credentialModalProject.id);
      }

      setIsCredentialFormOpen(false);
      setCredentialModalProject(null);
      setCredentialModalAgent(null);
      setEditingCredential(null);
    } catch (err) {
      throw err; // Let the form handle the error
    }
  }, [credentialModalProject, credentialModalAgent, editingCredential]);

  // Agent credential handlers
  const loadAgentCredentials = useCallback(async (agentId) => {
    if (agentCredentials[agentId]) return; // Already loaded

    setLoadingAgentCredentialsId(agentId);
    try {
      const credentials = await fetchAgentCredentials(agentId);
      setAgentCredentials(prev => ({
        ...prev,
        [agentId]: credentials
      }));
    } catch (err) {
      console.error('Failed to load agent credentials:', err);
    } finally {
      setLoadingAgentCredentialsId(null);
    }
  }, [agentCredentials]);

  const reloadAgentCredentials = useCallback(async (agentId) => {
    setLoadingAgentCredentialsId(agentId);
    try {
      const credentials = await fetchAgentCredentials(agentId);
      setAgentCredentials(prev => ({
        ...prev,
        [agentId]: credentials
      }));
    } catch (err) {
      console.error('Failed to reload agent credentials:', err);
    } finally {
      setLoadingAgentCredentialsId(null);
    }
  }, []);

  const handleAddAgentCredential = useCallback((agent) => {
    setCredentialModalAgent(agent);
    setCredentialModalProject(null);
    setEditingCredential(null);
    setIsCredentialFormOpen(true);
  }, []);

  const handleEditAgentCredential = useCallback((agent, credential) => {
    setCredentialModalAgent(agent);
    setCredentialModalProject(null);
    setEditingCredential(credential);
    setIsCredentialFormOpen(true);
  }, []);

  const handleDeleteAgentCredential = useCallback(async (agent, credential) => {
    if (!confirm(`Delete credential "${credential.name}"?`)) return;
    try {
      await deleteCredential(credential.id);
      await reloadAgentCredentials(agent.id);
    } catch (err) {
      alert(err.message);
    }
  }, [reloadAgentCredentials]);

  const filteredProjects = projects.filter((project) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      project.name?.toLowerCase().includes(query) ||
      project.displayName?.toLowerCase().includes(query) ||
      project.description?.toLowerCase().includes(query)
    );
  });

  const activeProjects = filteredProjects.filter(p => !p.isArchived);
  const archivedProjects = filteredProjects.filter(p => p.isArchived);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-500/10">
            <FolderKanban className="w-6 h-6 text-indigo-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">My Projects</h1>
            <p className="text-sm text-gray-400">
              {projects.length} project{projects.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setIsCreateModalOpen(true)}
            className="
              flex items-center gap-2 px-3 py-2 rounded-md
              text-white bg-indigo-600 hover:bg-indigo-500
              transition-colors
            "
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">New Project</span>
          </button>
          <button
            type="button"
            onClick={loadData}
            disabled={isLoading}
            className="
              p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors
            "
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-md">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search projects..."
        />
      </div>

      {/* Content */}
      {isLoading && projects.length === 0 ? (
        <LoadingSkeleton />
      ) : error ? (
        <ErrorState message={error} onRetry={loadData} />
      ) : filteredProjects.length === 0 ? (
        <EmptyState
          message={searchQuery ? 'No projects match your search' : 'No projects found'}
          description={searchQuery ? 'Try adjusting your search query.' : 'Projects will appear here once created.'}
        />
      ) : (
        <div className="space-y-8">
          {/* Active Projects */}
          {activeProjects.length > 0 && (
            <div>
              <h2 className="text-lg font-medium text-white mb-4">
                Active Projects ({activeProjects.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeProjects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    isExpanded={expandedProjectId === project.id}
                    onToggleExpand={() => handleToggleExpand(project.id)}
                    agents={projectDetails[project.id]?.agents}
                    channels={projectDetails[project.id]?.channels}
                    credentials={projectDetails[project.id]?.credentials}
                    agentCredentials={agentCredentials}
                    isLoadingDetails={isLoadingDetails && expandedProjectId === project.id}
                    isLoadingCredentials={isLoadingCredentials && expandedProjectId === project.id}
                    loadingAgentCredentialsId={loadingAgentCredentialsId}
                    onAssignAgent={() => setAssignModalProject(project)}
                    onRemoveAgent={(agentId) => handleRemoveAgent(project.id, agentId)}
                    removingAgentId={removingAgentId}
                    onAddCredential={() => handleAddCredential(project)}
                    onEditCredential={(cred) => handleEditCredential(project, cred)}
                    onDeleteCredential={(cred) => handleDeleteCredential(project, cred)}
                    onLoadAgentCredentials={loadAgentCredentials}
                    onAddAgentCredential={handleAddAgentCredential}
                    onEditAgentCredential={handleEditAgentCredential}
                    onDeleteAgentCredential={handleDeleteAgentCredential}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Archived Projects */}
          {archivedProjects.length > 0 && (
            <div>
              <h2 className="text-lg font-medium text-gray-400 mb-4">
                Archived ({archivedProjects.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {archivedProjects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    isExpanded={expandedProjectId === project.id}
                    onToggleExpand={() => handleToggleExpand(project.id)}
                    agents={projectDetails[project.id]?.agents}
                    channels={projectDetails[project.id]?.channels}
                    credentials={projectDetails[project.id]?.credentials}
                    agentCredentials={agentCredentials}
                    isLoadingDetails={isLoadingDetails && expandedProjectId === project.id}
                    isLoadingCredentials={isLoadingCredentials && expandedProjectId === project.id}
                    loadingAgentCredentialsId={loadingAgentCredentialsId}
                    onAssignAgent={() => setAssignModalProject(project)}
                    onRemoveAgent={(agentId) => handleRemoveAgent(project.id, agentId)}
                    removingAgentId={removingAgentId}
                    onAddCredential={() => handleAddCredential(project)}
                    onEditCredential={(cred) => handleEditCredential(project, cred)}
                    onDeleteCredential={(cred) => handleDeleteCredential(project, cred)}
                    onLoadAgentCredentials={loadAgentCredentials}
                    onAddAgentCredential={handleAddAgentCredential}
                    onEditAgentCredential={handleEditAgentCredential}
                    onDeleteAgentCredential={handleDeleteAgentCredential}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleCreateProject}
      />

      {/* Assign Agent Modal */}
      <AssignAgentModal
        isOpen={!!assignModalProject}
        onClose={() => setAssignModalProject(null)}
        project={assignModalProject}
        availableAgents={availableAgents}
        assignedAgentIds={
          assignModalProject
            ? (projectDetails[assignModalProject.id]?.agents || []).map(a => a.id)
            : []
        }
        roles={availableRoles}
        onAssign={handleAssignAgent}
      />

      {/* Credential Form Modal */}
      {isCredentialFormOpen && (
        <CredentialForm
          onClose={() => {
            setIsCredentialFormOpen(false);
            setCredentialModalProject(null);
            setCredentialModalAgent(null);
            setEditingCredential(null);
          }}
          onSubmit={handleCredentialSave}
          credential={editingCredential}
          scope={credentialModalAgent ? 'agent' : 'project'}
          projectId={credentialModalProject?.id}
          agentId={credentialModalAgent?.id}
        />
      )}
    </div>
  );
}

export { UserProjectsView };
